import { AlertTriangle, CheckCircle, AlertCircle } from "lucide-react";

/**
 * MessageRiskMeter - Displays a risk score with horizontal bar and traffic light
 * 
 * @param {number} riskScore - Score from 0-10
 * @param {string} riskLevel - "green", "yellow", or "red"
 * @param {string} shortReason - Brief explanation of the risk
 * @param {string} quickTip - Actionable tip to improve
 */
export default function MessageRiskMeter({ 
  riskScore = 0, 
  riskLevel = "green", 
  shortReason = "", 
  quickTip = "" 
}) {
  // Clamp score to 0-10 range
  const score = Math.max(0, Math.min(10, riskScore));
  const percentage = (score / 10) * 100;

  // Get colors and labels based on risk level
  const config = {
    green: {
      label: "Low Risk",
      barColor: "bg-emerald-500",
      textColor: "text-emerald-600",
      bgColor: "bg-emerald-50",
      borderColor: "border-emerald-200",
      icon: CheckCircle,
    },
    yellow: {
      label: "Moderate Risk",
      barColor: "bg-amber-500",
      textColor: "text-amber-600",
      bgColor: "bg-amber-50",
      borderColor: "border-amber-200",
      icon: AlertCircle,
    },
    red: {
      label: "High Risk",
      barColor: "bg-red-500",
      textColor: "text-red-600",
      bgColor: "bg-red-50",
      borderColor: "border-red-200",
      icon: AlertTriangle,
    },
  };

  const current = config[riskLevel] || config.green;
  const Icon = current.icon;

  return (
    <div 
      className={`rounded-2xl p-5 border ${current.borderColor} ${current.bgColor}`}
      data-testid="message-risk-meter"
    >
      {/* Header with Score and Traffic Light */}
      <div className="flex items-center justify-between mb-4">
        {/* Score Display */}
        <div className="flex items-center gap-3">
          <div className={`text-4xl font-bold ${current.textColor}`}>
            {score.toFixed(1)}
            <span className="text-lg font-normal text-gray-400">/10</span>
          </div>
          <div>
            <div className={`text-sm font-semibold ${current.textColor}`}>
              {current.label}
            </div>
            <div className="text-xs text-gray-500">Send Risk Score</div>
          </div>
        </div>

        {/* Traffic Light */}
        <div 
          className="flex flex-col gap-1.5 p-2 bg-gray-800 rounded-lg"
          data-testid="traffic-light"
        >
          <div 
            className={`w-5 h-5 rounded-full transition-all duration-300 ${
              riskLevel === "red" 
                ? "bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.6)]" 
                : "bg-red-900/40"
            }`}
            data-testid="traffic-red"
          />
          <div 
            className={`w-5 h-5 rounded-full transition-all duration-300 ${
              riskLevel === "yellow" 
                ? "bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.6)]" 
                : "bg-amber-900/40"
            }`}
            data-testid="traffic-yellow"
          />
          <div 
            className={`w-5 h-5 rounded-full transition-all duration-300 ${
              riskLevel === "green" 
                ? "bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.6)]" 
                : "bg-emerald-900/40"
            }`}
            data-testid="traffic-green"
          />
        </div>
      </div>

      {/* Horizontal Meter Bar */}
      <div className="mb-4">
        <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className={`h-full ${current.barColor} rounded-full transition-all duration-500 ease-out`}
            style={{ width: `${percentage}%` }}
            data-testid="risk-bar"
          />
        </div>
        {/* Scale markers */}
        <div className="flex justify-between mt-1 px-0.5">
          <span className="text-[10px] text-gray-400">0</span>
          <span className="text-[10px] text-gray-400">Safe</span>
          <span className="text-[10px] text-gray-400">5</span>
          <span className="text-[10px] text-gray-400">Risky</span>
          <span className="text-[10px] text-gray-400">10</span>
        </div>
      </div>

      {/* Short Reason */}
      {shortReason && (
        <div className="flex items-start gap-2 mb-3">
          <Icon className={`${current.textColor} flex-shrink-0 mt-0.5`} size={16} />
          <p className="text-sm text-gray-700 leading-relaxed" data-testid="short-reason">
            {shortReason}
          </p>
        </div>
      )}

      {/* Quick Tip */}
      {quickTip && (
        <div className="bg-white/60 rounded-xl p-3 border border-white">
          <p className="text-xs font-medium text-gray-500 mb-1">Quick tip:</p>
          <p className="text-sm text-gray-700" data-testid="quick-tip">
            {quickTip}
          </p>
        </div>
      )}
    </div>
  );
}
